/* eslint-disable no-console */
import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useState, useContext, useEffect } from 'react';
import { analytics } from "../../helpers/analytics";
import { trackCheckoutStart, trackPaywallView } from '../../helpers/metaPixel';

import { ArrowRight } from '../../Icons';
import { formatAmount } from '../../helpers/shared';
import { SignIn } from '../SignIn';
import { PriceContext } from '../../context/priceContext';

type Props = {
  userId?: string | null;
  email: string;
  isOpen: boolean;
  isTrial: boolean;
  hasPremium: boolean;
  onClose: () => void;
};

type Plan = {
  id: "annual" | "weekly";
  title: string;
  subtitle: string;
  priceId?: string;
  amount?: number;
  cadence: string;
  badge?: string;
  cta: string;
  featured?: boolean;
  metrics: string[];
  features: string[];
};

const billingOptions: Array<Plan["id"]> = ["weekly", "annual"];
const defaultAnnualAmount = 2999;
const defaultWeeklyAmount = 499;

const productRows = [
  "1,050+ Procreate brushes, stamps, palettes, fonts and assets",
  "Fresh creator drops and weekly library updates",
  "Premium downloads across brushes, stickers, 3D and interior packs",
  "Academy lessons and workflow references for Procreate artists",
];

const getMonthlyEquivalent = (amount?: number) => {
  if (!amount) {
    return "";
  }

  return `${formatAmount(amount / 12)} / mo`;
};

const getAnnualSavingsPercent = (weeklyAmount?: number, annualAmount?: number) => {
  if (!weeklyAmount || !annualAmount) {
    return null;
  }

  const weeklyAnnualCost = weeklyAmount * 52;
  const savings = Math.round((1 - annualAmount / weeklyAnnualCost) * 100);

  return savings > 0 ? savings : null;
};

const PaywallModal = ({ userId, isOpen, email, isTrial, hasPremium, onClose }: Props) => {
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<Plan["id"]>("annual");
  const [isCheckoutLoading, setCheckoutLoading] = useState(false);

  const { price } = useContext(PriceContext);

  const { weekly, annually } = price || {};
  const annualAmount = annually?.amount || defaultAnnualAmount;
  const weeklyAmount = weekly?.amount || defaultWeeklyAmount;
  const annualSavingsPercent = getAnnualSavingsPercent(weeklyAmount, annualAmount);
  const annualSavingsBadge = annualSavingsPercent ? `Save ${annualSavingsPercent}%` : "Best value";
  const sharedFeatures = [
    "Unlimited premium downloads",
    "All new weekly drops",
    "Academy and workflow guides",
    "Color palettes, fonts, stickers and 3D assets",
  ];

  const plans: Plan[] = [
    {
      id: "annual",
      title: "Unlimited Access",
      subtitle: "Full-year access to every premium pack and weekly drop",
      priceId: annually?.price,
      amount: annualAmount,
      cadence: "billed annually",
      badge: annualSavingsBadge,
      cta: "Get unlimited access",
      featured: true,
      metrics: [
        annualSavingsPercent ? `Save ${annualSavingsPercent}% vs weekly` : "Best yearly value",
        "Full-year access",
        "Lowest cost per brush pack",
      ],
      features: sharedFeatures,
    },
    {
      id: "weekly",
      title: "Unlimited Access",
      subtitle: "Weekly access to every premium pack and creator update",
      priceId: weekly?.price,
      amount: weeklyAmount,
      cadence: "cancel anytime",
      badge: "Weekly",
      cta: "Get unlimited access",
      metrics: [
        "Weekly billing",
        "Instant downloads",
        "Cancel anytime",
      ],
      features: sharedFeatures,
    },
  ];
  const selectedPlan = plans.find((plan) => plan.id === selectedBilling) || plans[0];
  const selectedPriceLabel = formatAmount(selectedPlan.amount || 0);
  const isCheckoutReady = Boolean(selectedPlan.priceId) && !isCheckoutLoading;

  useEffect(() => {
    if (isOpen) {
      analytics.track("Paywall Viewed", {
        premium: hasPremium,
        trial: isTrial,
        selected_billing: selectedBilling,
        annual_price_available: Boolean(annually?.price),
        weekly_price_available: Boolean(weekly?.price),
      });
      trackPaywallView({
        premium: hasPremium,
        trial: isTrial,
        selected_billing: selectedBilling,
      });
      setTimeout(async () => {
        // @ts-ignore
        gtag('event', 'begin_checkout', {
          'transaction_id': Math.random() * 100000,
          currency: "USD",
          value: 0,
          items: [
            {
              item_id: "PaywallOpened",
              item_name: "Paywall opened",
            }
          ]
        });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const trackSelectedPlan = async (selectedPlan: Plan) => {
    analytics.track("Checkout Started", {
      plan: selectedPlan.id,
      amount: selectedPlan.amount,
      price_id: selectedPlan.priceId,
      premium: hasPremium,
      trial: isTrial,
    });

    if (selectedPlan.id === "annual") {
      analytics.track('Annual subscription tapped', {
        premium: hasPremium,
        trial: isTrial,
      });
      trackCheckoutStart({
        plan: "annual_subscription",
        amount: selectedPlan.amount,
      });
      localStorage.setItem('last_purchase', selectedPlan.amount ? String(selectedPlan.amount / 100) : '29.99');

    } else if (selectedPlan.id === "weekly") {
      analytics.track('Weekly subscription tapped', {
        premium: hasPremium,
        trial: isTrial,
      });
      trackCheckoutStart({
        plan: "weekly_subscription",
        amount: selectedPlan.amount,
      });
      localStorage.setItem('last_purchase', selectedPlan.amount ? String(selectedPlan.amount / 100) : '4.99');
    }
  };

  const handlePlanClick = async (selectedPlan: Plan) => {
    if (!selectedPlan.priceId) {
      analytics.track("Checkout Unavailable", {
        plan: selectedPlan.id,
        reason: "missing_price_id",
      });
      return;
    }

    setCheckoutLoading(true);
    try {
      await trackSelectedPlan(selectedPlan);

      if (userId) {
        const data = {
          sku: selectedPlan.priceId,
          userId,
          email,
          source: "paywall_modal",
          variationId: `paywall_modal_${selectedPlan.id}`,
        };
        const response = await fetch('/.netlify/functions/create-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }).then((res) => res.json());

        if (response.url) {
          analytics.track("Checkout Created", {
            plan: selectedPlan.id,
            amount: selectedPlan.amount,
            price_id: selectedPlan.priceId,
          });
          window.location.assign(response.url);
          return;
        }

        analytics.track("Checkout Failed", {
          plan: selectedPlan.id,
          amount: selectedPlan.amount,
          price_id: selectedPlan.priceId,
          error_message: response.error || "Checkout response missing url",
        });
        console.error(response.error || "Checkout failed");
      } else {
        analytics.track("Auth Required", {
          source: "paywall_checkout",
          plan: selectedPlan.id,
        });
        setAuthOpen(true);
      }
    } catch (error) {
      analytics.track("Checkout Failed", {
        plan: selectedPlan.id,
        amount: selectedPlan.amount,
        price_id: selectedPlan.priceId,
        error_message: error instanceof Error ? error.message : String(error),
      });
      console.error(error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    analytics.track("Auth Gate Completed", {
      source: "paywall_checkout",
      plan: selectedBilling,
    });
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
              relative 
            inline-block 
            overflow-hidden 
            transform 
            transition-all
            align-middle 
            w-[1120px]
            md:w-full
            md:max-w-[420px]
            `}
            >
              <div className='relative max-h-[95vh] overflow-y-auto rounded-[24px] bg-[#0D1012] text-left text-white shadow-[0_24px_80px_rgba(0,0,0,0.45)] md:rounded-[20px]'>
                  <button
                  type='button'
                  aria-label='Close paywall'
                  onClick={() => {
                    analytics.track("Paywall Closed", {
                      selected_billing: selectedBilling,
                    });
                    onClose();
                  }}
                  className='absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl leading-none text-black md:right-4 md:top-4'
                >
                  x
                </button>

                <div className='bg-paywall-pattern bg-cover bg-center'>
                  <div className='min-h-[250px] bg-gradient-to-r from-black/80 via-black/50 to-black/20 p-8 pr-20 md:min-h-[210px] md:p-5 md:pr-16'>
                    <div className='mb-5 inline-flex rounded-[6px] bg-[#FF1875] px-3 py-1 text-xxs font-bold uppercase text-white'>
                      {selectedBilling === "annual" && annualSavingsPercent
                        ? `Save ${annualSavingsPercent}% vs weekly`
                        : "Unlimited access"}
                    </div>
                    <Dialog.Title className='max-w-[760px] text-5xl font-bold leading-5xl text-white md:text-4xl md:leading-4xl'>
                      Unlimited Access
                    </Dialog.Title>
                    <p className='mt-4 max-w-[690px] text-lg leading-lg text-white/80 md:text-sm md:leading-sm'>
                      Unlock brushes, stamps, palettes, fonts, 3D references, interior packs and Academy lessons in one creator library.
                    </p>
                  </div>
                </div>

                <div className='p-6 md:p-4'>
                  <div className='mb-6 flex justify-center md:mb-4'>
                    <div className='flex items-center justify-center gap-10 text-sm font-bold text-white/50 md:gap-8'>
                      {billingOptions.map((billing) => {
                        const isSelected = selectedBilling === billing;

                        return (
                          <button
                            key={billing}
                            type='button'
                            aria-pressed={isSelected}
                            onClick={() => {
                              setSelectedBilling(billing);
                              analytics.track("Billing Plan Selected", {
                                plan: billing,
                                source: "paywall_tabs",
                              });
                            }}
                            className={`
                              py-2 transition
                              ${isSelected ? "text-white" : "hover:text-white"}
                            `}
                          >
                            <span>{billing === "annual" ? "Annual" : "Weekly"}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className='mx-auto max-w-[700px]'>
                    <div
                      className={`
                        rounded-[18px] border p-5 text-left
                        ${selectedPlan.featured
                          ? "border-[#FF1875] bg-[#201019] shadow-[0_0_0_2px_rgba(255,24,117,0.35)]"
                          : "border-white/10 bg-[#181C1F]"}
                      `}
                    >
                      <div className='flex min-h-full flex-col'>
                        <div className='mb-5 flex items-center justify-between gap-3'>
                          <div>
                            <div className='text-2xl font-bold leading-2xl text-white'>
                              {selectedPlan.title}
                            </div>
                            <div className='mt-1 text-xs leading-xs text-white/60'>
                              {selectedPlan.subtitle}
                            </div>
                          </div>
                          <span className={`${selectedPlan.featured ? "bg-[#FF1875] text-white" : "bg-white/10 text-white"} shrink-0 rounded-full px-3 py-1 text-xxs font-bold uppercase`}>
                            {selectedPlan.badge}
                          </span>
                        </div>

                        <div className='mb-5 rounded-[10px] bg-white/10 p-4'>
                          <div className='flex items-end gap-2'>
                            <span className='text-5xl font-bold leading-none text-white md:text-4xl'>
                              {selectedPriceLabel}
                            </span>
                            <span className='pb-1 text-sm font-bold text-white/60'>
                              {selectedPlan.id === "annual" ? "/ year" : "/ week"}
                            </span>
                          </div>
                          <div className='mt-2 text-sm leading-sm text-white/60'>
                            {selectedPlan.id === "annual" ? getMonthlyEquivalent(selectedPlan.amount) : selectedPlan.cadence}
                          </div>
                        </div>

                        <div className='mb-5 grid grid-cols-3 gap-2 md:grid-cols-1'>
                          {selectedPlan.metrics.map((metric) => (
                            <div key={metric} className='rounded-[8px] bg-white/10 p-3 text-xs font-bold leading-xs text-white/80'>
                              {metric}
                            </div>
                          ))}
                        </div>

                        <ul className='mb-6 flex flex-col gap-3 text-sm leading-sm text-white/80'>
                          {selectedPlan.features.map((feature) => (
                            <li key={feature} className='flex gap-2'>
                              <span className='mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#C8FF1A]' />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          type='button'
                          disabled={!isCheckoutReady}
                          onClick={() => handlePlanClick(selectedPlan)}
                          className={`
                            group mt-auto flex w-full items-center justify-between rounded-[12px] px-4 py-3 text-sm font-bold transition
                            ${isCheckoutReady
                              ? "bg-[#C8FF1A] text-black hover:bg-white"
                              : "cursor-not-allowed bg-white/30 text-white/60"}
                          `}
                        >
                          <span>
                            {isCheckoutLoading
                              ? "Opening checkout..."
                              : selectedPlan.priceId
                              ? selectedPlan.cta
                              : "Checkout unavailable"}
                          </span>
                          <span className='text-main-blue transition group-hover:translate-x-1'>
                            <ArrowRight width={24} height={24} />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className='mt-4 grid grid-cols-4 gap-3 md:grid-cols-1'>
                    {productRows.map((row) => (
                      <div key={row} className='rounded-[10px] border border-white/10 bg-white/5 p-3 text-xs font-bold leading-xs text-white/70'>
                        {row}
                      </div>
                    ))}
                  </div>

                  <p className='mt-6 text-center text-sm text-white/70 md:text-xs'>
                    <a
                      className='underline hover:no-underline'
                      href='https://endurable-weaver-d97.notion.site/Terms-Conditions-Tools-fbdcabcb12d942948055c022c3cf226b'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Terms of Use
                    </a>{' '}
                    &amp;{' '}
                    <a
                      className='underline hover:no-underline'
                      href='https://endurable-weaver-d97.notion.site/Privacy-Policy-Tools-Products-87ad109325ae476a903172a44f41b393'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
        <SignIn isOpen={isAuthOpen} onClose={handleAuthClose} onSuccess={handleAuthSuccess} />
      </Dialog>
    </Transition.Root>
  );
};

export { PaywallModal };
